/**
 * Birden fazla tanım ekranının paylaştığı sunucu tipleri ve sorguları.
 *
 * Tipler apps/api/src/moduller/*.rotalari.ts içindeki YANIT ŞEKİLLERİNDEN
 * çıkarılmıştır; tahmin edilmemiştir. Para ve miktar alanları sunucuda
 * paraStr()/toFixed() ile üretildiği için BURADA DA string'dir (ADR-03).
 */
import { useQuery } from "@tanstack/react-query";
import { apiOku } from "./api.js";
import { urunListesiniOku } from "./urunListesi.js";

// ---------------------------------------------------------------------------
// Ortak yanıt tipleri
// ---------------------------------------------------------------------------

export interface Nokta {
  id: string;
  ad: string;
  tur: number;
  satis_yapar: boolean;
  cuzdan_turu: number | null;
  sorumlu_personel_id: string | null;
  sorumlu_ad: string | null;
  /** numeric(5,2) — string olarak döner */
  hakedis_orani: string | null;
  fis_politikasi: number;
  aktif: boolean;
  terminal_sayisi: number;
}

export interface PersonelNoktaAtamasi {
  nokta_id: string;
  nokta_ad: string;
  rol: number;
  baslangic: string | null;
  bitis: string | null;
  vekalet: boolean;
  gecerli: boolean;
}

export interface Personel {
  id: string;
  ad: string;
  soyad: string;
  eposta: string | null;
  telefon: string | null;
  rol: number;
  aktif: boolean;
  parola_kurulu: boolean;
  pin_kurulu: boolean;
  totp_kurulu: boolean;
  noktalar: PersonelNoktaAtamasi[] | null;
}

export interface Kategori {
  id: string;
  ad: string;
  ust_id: string | null;
  sira: number;
  aktif: boolean;
  urun_sayisi: number;
}

export interface Urun {
  id: string;
  ad: string;
  kategori_id: string | null;
  kategori_ad: string | null;
  satis_fiyat: string;
  alis_fiyat: string;
  kdv_orani: string | null;
  birim: string;
  kritik_seviye: string | null;
  alerjenler: string[];
  gorsel_url: string | null;
  hizli_buton_sira: number | null;
  skt_takibi: boolean;
  ozel_siparis: boolean;
  raf_omru_gun: number | null;
  iade_esigi_gun: number;
  aktif: boolean;
  satilabilir: boolean;
  barkodlar: string[];
}

export interface Tedarikci {
  id: string;
  ad: string;
  vergi_no: string | null;
  telefon: string | null;
  eposta: string | null;
  adres: string | null;
  aktif: boolean;
  bakiye: string;
  gecikmis_tutar: string;
  en_yakin_vade: string | null;
  son_hareket: string | null;
}

export interface Okul {
  id: string;
  logo_medya_id: string | null;
  ad: string;
  kod: string;
  saat_dilimi: string;
  para_birimi: string;
  eksi_tolerans: string;
  fotosuz_offline_tavan: string | null;
  kart_yenileme_ucreti: string;
  gun_sonu_mail_saati: string | null;
  kullanilabilir_roller: number[];
  /** Platform yöneticisinin kapattığı roller; okul bunları açamaz (0021). */
  platform_kapali_roller: number[];
  aktif: boolean;
}

// ---------------------------------------------------------------------------
// Sorgular
// ---------------------------------------------------------------------------

export function useNoktalar(secenek?: { aktif?: boolean }) {
  const aktif = secenek?.aktif;
  return useQuery({
    queryKey: ["nokta", aktif ?? null],
    queryFn: () =>
      apiOku<{ noktalar: Nokta[] }>("/nokta", {
        aktif: aktif === undefined ? undefined : aktif,
      }),
    staleTime: 60_000,
  });
}

export function usePersoneller(secenek?: { aktif?: boolean }) {
  const aktif = secenek?.aktif;
  return useQuery({
    queryKey: ["personel", aktif ?? null],
    queryFn: () =>
      apiOku<{ personeller: Personel[] }>("/personel", {
        aktif: aktif === undefined ? undefined : aktif,
      }),
    staleTime: 60_000,
  });
}

/**
 * Ürün listesi. Sunucudaki GET /urun (stok.rotalari.ts, zUrunSorgu) yalnızca
 * arama / kategori_id / nokta_id / aktif / limit / offset kabul eder; bu yüzden
 * `satilabilir` sunucuya GÖNDERİLMEZ, yanıt üzerinde süzülür.
 *
 * Limit verilmezse tüm sayfalar yüklenir; barkod araması katalog boyunca çalışır.
 */
export function useUrunler(secenek?: {
  aktif?: boolean;
  satilabilir?: boolean;
  limit?: number;
}) {
  const aktif = secenek?.aktif;
  const satilabilir = secenek?.satilabilir;
  const limit = secenek?.limit;
  return useQuery({
    // `satilabilir` anahtara GİRMEZ: istekte gönderilmiyor, aşağıda `select`
    // ile süzülüyor. Anahtarda tutmak aynı veriyi iki kez çektirir.
    queryKey: ["urun", aktif ?? null, limit ?? "tumu"],
    queryFn: () => urunListesiniOku(aktif, limit),
    select: (veri) =>
      satilabilir === undefined
        ? veri
        : {
            urunler: veri.urunler.filter((u) => u.satilabilir === satilabilir),
          },
    staleTime: 60_000,
  });
}

export function useKategoriler() {
  return useQuery({
    queryKey: ["kategori"],
    queryFn: () => apiOku<{ kategoriler: Kategori[] }>("/kategori"),
    staleTime: 300_000,
  });
}

export function useTedarikciler(arama?: string) {
  return useQuery({
    queryKey: ["tedarikci", arama ?? ""],
    queryFn: () =>
      apiOku<{ tedarikciler: Tedarikci[]; toplam_borc: string }>("/tedarikci", {
        arama: arama || undefined,
      }),
    staleTime: 30_000,
  });
}

export function useOkul() {
  return useQuery({
    queryKey: ["okul"],
    queryFn: () => apiOku<Okul>("/okul"),
    staleTime: 300_000,
  });
}

/** Seçim bileşenleri "ilk seçenek boş" ister; yardımcı burada tek yerde durur. */
export function noktaSecenekleri(
  noktalar: Nokta[] | undefined,
  bosMetin = "Tüm noktalar",
): { deger: string; metin: string }[] {
  return [
    { deger: "", metin: bosMetin },
    ...(noktalar ?? []).map((n) => ({
      deger: n.id,
      metin: n.satis_yapar ? n.ad : `${n.ad} (depo)`,
    })),
  ];
}

/**
 * Barkodu ürüne çevirir — okul içinde barkod TEKİLDİR (urun_barkod tablosunun
 * birincil anahtarı `(okul_id, barkod)`), bu yüzden ilk eşleşme kesin sonuçtur.
 *
 * Karşılaştırma TAM eşleşmedir: sunucu da (`GET /urun`, stok.rotalari.ts) ve
 * kasa da (SatisEkrani.tsx) barkodu tam eşleştirir. Burada "içerir" ya da
 * "büyük/küçük harf duyarsız" davranmak, panelin kasayla FARKLI ürünü bulması
 * demek olurdu.
 */
export function barkodBul(
  urunler: Urun[] | undefined,
  barkod: string,
): Urun | null {
  const d = barkod.trim();
  if (d === "") return null;
  return (urunler ?? []).find((u) => u.barkodlar.includes(d)) ?? null;
}

/** Ürün seçim kutuları için; Transferler ekranıyla aynı biçim (yalnız ad). */
export function urunSecenekleri(
  urunler: Urun[] | undefined,
  bosMetin = "Ürün seçin",
): { deger: string; metin: string }[] {
  return [
    { deger: "", metin: bosMetin },
    ...(urunler ?? []).map((u) => ({ deger: u.id, metin: u.ad })),
  ];
}
